// src/pages/InvitationsPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import api from '../utils/api';
import '../styles/InvitationsPage.css';
import config from '../config';
import { useToast } from '../contexts/ToastContext';

const InvitationsPage = () => {
  useTheme(); // Initialize theme for this page
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const { showSuccess, showError } = useToast();

  const invitationsUrl = `${config.apiBaseUrl}/api/invitations`;

  useEffect(() => {
    const fetchInvitations = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found, redirecting to login');
        navigate('/login');
        return;
      }

      console.log('InvitationsPage: fetching from', invitationsUrl);
      try {
        const res = await api.get('/api/invitations', {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('InvitationsPage: response data', res.data);
        setInvitations(res.data);
      } catch (error) {
        console.error('Error fetching invitations', error);
        setInvitations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInvitations();
  }, [navigate, invitationsUrl]);

  const handleAccept = async (invId) => {
    const token = localStorage.getItem('token');
    try {
      await api.post(`/api/invitations/${invId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(prev => prev.filter(inv => inv._id !== invId));
      showSuccess('Invitation accepted!');
    } catch (error) {
      console.error('Error accepting invitation', error);
      showError('Failed to accept invitation.');
    }
  };

  const handleReject = async (invId) => {
    const token = localStorage.getItem('token');
    try {
      try {
        await api.post(`/api/invitations/${invId}/decline`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        // Backward compatible fallback (older servers)
        await api.delete(`/api/invitations/${invId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setInvitations(prev => prev.filter(inv => inv._id !== invId));
      showSuccess('Invitation denied.');
    } catch (error) {
      console.error('Error denying invitation', error);
      showError('Failed to deny invitation.');
    }
  };

  const handleBackClick = () => {
    setIsNavigating(true);
    navigate('/contacts');
  };

  return (
    <div className="invitations-page">
      <h2>Invitations</h2>
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading invitations...</p>
        </div>
      ) : !Array.isArray(invitations) ? (
        <p className="no-invitations-message">Unable to load invitations.</p>
      ) : invitations.length === 0 ? (
        <p className="no-invitations-message">No pending invitations.</p>
      ) : (
        <div className="invitations-list">
          {invitations.map(invitation => (
            <div key={invitation._id} className="invitation-card">
              <p><strong>{invitation.name}</strong></p>
              {invitation.company && <p>{invitation.company}</p>}
              {invitation.role && <p>{invitation.role}</p>}
              <p>{invitation.email}</p>
              <div className="invitation-actions">
                <button onClick={() => handleAccept(invitation._id)}>Accept</button>
                <button onClick={() => handleReject(invitation._id)}>Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {!isNavigating && (
        <button className="invitations-to-contacts-button" onClick={handleBackClick}>
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Contacts
        </button>
      )}
    </div>
  );
};

export default InvitationsPage;
